import { Dialog, FormSectionHeader, FormSelect } from "@/components";
import { CopyDistributionConfigProps } from "./types";
import useCopyDistributionConfig from "./useCopyDistributionConfig";
import { FormProvider } from "react-hook-form";
import { FROM, TO } from "./consts";
import { yearOptions } from "@/lib/utils";
import { Info } from "lucide-react";
import { Card, CardContent } from "@/components/ui";

const CopyDistributionConfig = ({open, setOpen}: CopyDistributionConfigProps) => {
  const { form, requiredFields, onConfirmCopy,
    isCopyPending, skipData, fromData } = useCopyDistributionConfig({ setOpen });
  const {errors} = form.formState;

  return (
    <Dialog
      open={open}
      setOpen={setOpen}
      title={<div className="text-2xl font-bold text-primary">
        {"Copy Allocation Configuration"}
        <div className="h-1 w-[30%] bg-primary rounded-full" />
      </div>}
      hideFooter={!!skipData}
      onSave={form.handleSubmit(onConfirmCopy)}
      isLoading={isCopyPending}
      contentClassName="lg:!max-w-[800px] lg:!w-[800px]"
    >
      <FormProvider {...form}>
        <form>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <FormSelect
                name={FROM}
                control={form.control}
                isRequired={requiredFields?.includes(FROM)}
                error={(errors?.[FROM] as any)?.message}
                showSearch={false}
                placeholder="Please Select"
                label="Copy From"
                options={yearOptions(10, 3)?.map((d: any) => ({
                  value: d.value?.toString(),
                  label: d.label,
                }))}
              />
              <span className="text-[12px] text-gray-500">Years with configured distribution rule</span>
            </div>
            <FormSelect
              name={TO}
              control={form.control}
              isRequired={requiredFields?.includes(FROM)}
              error={(errors?.[TO] as any)?.message}
              showSearch={false}
              placeholder="Please Select"
              label="Copy To"
              options={yearOptions(10, 3)?.map((d: any) => ({
                value: d.value?.toString(),
                label: d.label,
                disabled: d.value < Number(fromData[FROM])
              }))}
            />
          </div>
        </form>
      </FormProvider>

      {skipData ? <div className="mt-5 grid grid-cols-1 gap-5">
        <div className="bg-amber-50 border border-amber-500 text-amber-900 rounded-md p-3">
          <FormSectionHeader className="flex flex-row gap-2" wrapperClassName="mb-1">
            <Info />
            <span>Budget difference detected</span>
          </FormSectionHeader>
          <span className="text-[12px]">
            The {fromData[FROM]} and {fromData[TO]} budgets differ. GL-specific distribution rules will not be copied. <br /> Configure them against the {fromData[TO]} budget after the copy is complete.
          </span>
        </div>

        <Card className="shadow-none border-amber-500 bg-amber-50 text-amber-900 text-[12px] p-0">
          <CardContent className="p-2">
            <ul className="my-4 list-disc pl-4 gap-3">
              {[...skipData?.glMappingDifferences, ...skipData?.skippedAssignmentReasons, ...skipData?.skippedReasons]?.map((data: any) => {
                return <li>{data}</li>
              })}
            </ul>
          </CardContent>
        </Card>
        <div className="bg-blue-50 text-primary rounded-md p-3 text-[12px]">
          The source year's settings will remain unchanged.
        </div>
      </div> : <></>}
    </Dialog>
  );
};

export default CopyDistributionConfig;
